import { useCallback, useEffect, useState } from 'preact/hooks'
import { i18n } from 'i18n/i18n'

import { CurrencyMinimalAmount, CurrencyRatesPrecision, SettingsKeys } from 'models/settings'
import { Currency, SpinnerError } from 'models/calculator'

import { fetchSettings } from 'services/settings'
import { isHttpErrorStatus } from 'utils/promise'

export type SettingsConfig = {
  SETTINGS_KEYS: SettingsKeys[]
  DEFAULT_MINIMAL_AMOUNT: CurrencyMinimalAmount
  PLN_MINIMAL_AMOUNT: CurrencyMinimalAmount
  DEFAULT_CURRENCY: Currency
}

interface SettingsHookProps {
  setSpinnerError: (error: SpinnerError) => void
  config: SettingsConfig
}

export const useSettings = ({ setSpinnerError, config }: SettingsHookProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCurrentCurrencySupported, setIsCurrentCurrencySupported] = useState(true)
  const [supportedCurrenciesPairs, setSupportedCurrenciesPairs] = useState<string[]>([])
  const [supportedCurrenciesPairsAllCombinations, setSupportedCurrenciesPairsAllCombinations] = useState<string[]>([])
  const [currency, setCurrency] = useState<Currency>(config.DEFAULT_CURRENCY)
  const [minimalAmounts, setMinimalAmounts] = useState<CurrencyMinimalAmount[]>([config.DEFAULT_MINIMAL_AMOUNT])
  const [exchangeRatePrecision, setExchangeRatePrecision] = useState<CurrencyRatesPrecision>({})

  useEffect(() => {
    const fetchSupportedCurrency = async () => {
      try {
        const response = await fetchSettings(config.SETTINGS_KEYS)

        const currency = {
          symbols: response['currency']?.['symbols'] ?? config.DEFAULT_CURRENCY.symbols,
          symbolsSorted: response['currency']?.['symbols_sorted'] ?? config.DEFAULT_CURRENCY.symbolsSorted,
          defaultCurrency: response['currency']?.['default_currency'] ?? config.DEFAULT_CURRENCY.defaultCurrency,
        }
        setCurrency(currency)

        // The API returns supported currency pairs in the "market-currency-pairs" section under the name "pairs"
        const supportedPairs: string[] = response['market-currency-pairs']?.['pairs'] ?? []
        const allCombinationsPairs: string[] = []

        supportedPairs.forEach((pair: string) => {
          const base = pair.slice(3)
          const counter = pair.slice(0, 3)

          if (
            isCurrencySupported(base, currency.symbolsSorted) &&
            isCurrencySupported(counter, currency.symbolsSorted)
          ) {
            allCombinationsPairs.push(`${base}${counter}`, `${counter}${base}`)
          }
        })
        setSupportedCurrenciesPairs(supportedPairs)
        setSupportedCurrenciesPairsAllCombinations(allCombinationsPairs)

        // The API returns minimal amounts in the "direct-exchange-minimal-amounts" section under the name "minimal_amounts"
        const responseMinimalAmounts = response['direct-exchange-minimal-amounts']?.['minimal_amounts'] ?? [
          config.DEFAULT_MINIMAL_AMOUNT,
        ]
        setMinimalAmounts([...responseMinimalAmounts, config.PLN_MINIMAL_AMOUNT])

        // The API returns exchange rate precision in the "market-currency-pairs" section under the name "exchange_rate_precision"
        const responseExchangeRatePrecision = response['market-currency-pairs']?.['exchange_rate_precision'] ?? {}
        setExchangeRatePrecision(responseExchangeRatePrecision)
      } catch (e: unknown) {
        if (isHttpErrorStatus(e, 429)) {
          setSpinnerError({
            isErrorVisible: true,
            message: i18n('calculator.spinnerError.toManyRequests'),
          })
        } else {
          setSpinnerError({ isErrorVisible: true })
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchSupportedCurrency()
  }, [])

  const isCurrencyPairSupported = useCallback(
    (baseCurrency: string, counterCurrency: string): boolean => {
      const userPair = `${baseCurrency}${counterCurrency}`
      const supported = supportedCurrenciesPairs.includes(userPair)
      return supported
    },
    [supportedCurrenciesPairs],
  )

  const isCurrencySupported = (symbol: string, symbolsArg?: string[]): boolean => {
    const currencies = symbolsArg ?? currency.symbolsSorted
    return currencies.includes(symbol)
  }

  return {
    isSettingsLoading: isLoading,
    isCurrentCurrencySupported,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isCurrencySupported,
    supportedCurrenciesPairs,
    supportedCurrenciesPairsAllCombinations,
    currency,
    minimalAmounts,
    exchangeRatePrecision,
  }
}
